import React from 'react';
import { useTranslation } from 'react-i18next';
import { Search, UserPlus, User, Mail, Phone, X, Save, ChevronLeft, ChevronRight } from 'lucide-react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../context/AuthContext';
import PhoneInput from '../components/PhoneInput';

import { CustomerRow, useCustomers, useCustomersStore } from '../features/customers';

// ── Pagination helper ─────────────────────────────────────────────────────────

function getPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const range = new Set([1, total]);
  for (let i = Math.max(2, current - 2); i <= Math.min(total - 1, current + 2); i++) range.add(i);
  const sorted = [...range].sort((a, b) => a - b);
  const result = [];
  sorted.forEach((n, i) => {
    if (i > 0 && n - sorted[i - 1] > 1) result.push('…');
    result.push(n);
  });
  return result;
}

// ── Customers Page ────────────────────────────────────────────────────────────

export default function Customers() {
  const { user } = useAuth();
  const { t } = useTranslation('customers');
  const isAdmin = user?.role === 'admin';

  const {
    employees, filteredCustomers, saving,
    save, remove, backfill, triggerExpiryCheck,
  } = useCustomers();

  // UI state — Zustand
  const searchTerm    = useCustomersStore(s => s.searchTerm);
  const setSearchTerm = useCustomersStore(s => s.setSearchTerm);
  const currentPage   = useCustomersStore(s => s.currentPage);
  const itemsPerPage  = useCustomersStore(s => s.itemsPerPage);
  const setCurrentPage  = useCustomersStore(s => s.setCurrentPage);
  const setItemsPerPage = useCustomersStore(s => s.setItemsPerPage);
  const isModalOpen     = useCustomersStore(s => s.isModalOpen);
  const isEditing       = useCustomersStore(s => s.isEditing);
  const formData        = useCustomersStore(s => s.formData);
  const openModal       = useCustomersStore(s => s.openModal);
  const closeModal      = useCustomersStore(s => s.closeModal);
  const updateFormField = useCustomersStore(s => s.updateFormField);

  // Pagination
  const totalPages   = Math.ceil(filteredCustomers.length / itemsPerPage);
  const pageCustomers = filteredCustomers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <div className="p-6">
      <ToastContainer position="top-right" />

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <div className="relative w-full md:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder={t('search.placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex items-center gap-4">
          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {[10, 20, 50, 100].map(n => (
              <option key={n} value={n}>{t('perPage', { count: n })}</option>
            ))}
          </select>
          <button
            onClick={() => openModal()}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-md hover:from-cyan-600 hover:to-blue-600 transition-all"
          >
            <UserPlus className="h-5 w-5" />
            {t('newCustomer')}
          </button>
        </div>
      </div>

      {/* ── Tablo ──────────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['table.firstName','table.lastName','table.email','table.phone','table.points','table.actions'].map(k => (
                  <th key={k} className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${k === 'table.actions' ? 'text-right' : 'text-left'}`}>
                    {t(k)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pageCustomers.map((customer) => (
                <CustomerRow
                  key={customer.id}
                  customer={customer}
                  employees={employees}
                  isAdmin={isAdmin}
                  onBackfill={backfill}
                  onDelete={remove}
                  onExpand={triggerExpiryCheck}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Pagination ─────────────────────────────────────────────────────── */}
      <div className="mt-4 flex justify-between items-center">
        <p className="text-sm text-gray-700">
          {t('pagination.showing', {
            total: filteredCustomers.length,
            start: filteredCustomers.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1,
            end: Math.min(currentPage * itemsPerPage, filteredCustomers.length),
          })}
        </p>
        <div className="flex items-center gap-1">
          <button onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}
            className="px-2 py-1 border rounded-md disabled:opacity-50 hover:bg-gray-50">
            <ChevronLeft className="h-5 w-5" />
          </button>
          {getPageNumbers(currentPage, totalPages).map((n, i) =>
            n === '…' ? (
              <span key={`d${i}`} className="px-2 py-1">…</span>
            ) : (
              <button key={n} onClick={() => setCurrentPage(n)}
                className={`px-3 py-1 border rounded-md ${currentPage === n ? 'bg-blue-500 text-white' : 'hover:bg-gray-50'}`}>
                {n}
              </button>
            )
          )}
          <button onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage >= totalPages}
            className="px-2 py-1 border rounded-md disabled:opacity-50 hover:bg-gray-50">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* ── Müşteri Modal ───────────────────────────────────────────────────── */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/30" onClick={closeModal} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-[500px] mx-4">
            <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-4 flex justify-between items-center rounded-t-lg">
              <h2 className="text-xl font-semibold text-white">
                {isEditing ? t('modal.titleEdit') : t('modal.titleNew')}
              </h2>
              <button onClick={closeModal} className="text-white hover:text-gray-200">
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {[
                { key: 'name',         icon: User, label: 'modal.firstNameLabel', ph: 'modal.firstNamePlaceholder', type: 'text' },
                { key: 'surname',      icon: User, label: 'modal.lastNameLabel',  ph: 'modal.lastNamePlaceholder',  type: 'text' },
                { key: 'emailAddress', icon: Mail, label: 'modal.emailLabel',     ph: 'modal.emailPlaceholder',     type: 'email' },
              ].map(({ key, icon: Icon, label, ph, type }) => (
                <div key={key} className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <Icon className="h-4 w-4 text-blue-500" />
                    {t(label)}
                  </label>
                  <input
                    type={type}
                    placeholder={t(ph)}
                    value={formData[key] || ''}
                    onChange={(e) => updateFormField(key, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              ))}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <Phone className="h-4 w-4 text-blue-500" />
                  {t('modal.phoneLabel')}
                </label>
                <PhoneInput
                  value={formData.phone}
                  onChange={(v) => updateFormField('phone', v)}
                />
              </div>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 rounded-b-lg">
              <button onClick={closeModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md">
                {t('actions.cancel')}
              </button>
              <button onClick={save} disabled={saving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 rounded-md disabled:opacity-60">
                <Save className="h-4 w-4" />
                {isEditing ? t('actions.update') : t('actions.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
